<template>
    <div
        class="dashboard-template-category-item"
        :class="{ 'is--active': isActive }"
        @click="onSelect"
    >
        <div class="dashboard-template-category-item__content">
            <div class="dashboard-template-category-item__info">
                <span class="dashboard-template-category-item__name">
                    {{ category.DashboardTemplateCategoryName }}
                </span>
                <span class="dashboard-template-category-item__count">
                    {{ templatesCount }}
                </span>
            </div>
            <RedesignBaseIcon
                name="ChevronRight"
                :size="16"
                class="dashboard-template-category-item__chevron"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { DashboardTemplateCategoryList } from '@/types/dashboard-template'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE } from '@/enum/default-layout-config'

const props = withDefaults(
    defineProps<{
        category: DashboardTemplateCategoryList
        isActive?: boolean
        languageFilterValue?: number
    }>(),
    {
        languageFilterValue: LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE
    }
)

const emit = defineEmits<{
    (e: 'select', category: DashboardTemplateCategoryList): void
}>()

const templatesCount = computed(() => {
    const templates = props.category.DashboardTemplateList || []
    
    if (props.languageFilterValue === LAYOUT_TEMPLATES_ALL_LANGUAGES_FILTER_VALUE) {
        return templates.length
    }
    
    return templates.filter(template => template.LanguageID === props.languageFilterValue).length
})

function onSelect() {
    emit('select', props.category)
}
</script>

<style lang="scss" scoped>
.dashboard-template-category-item {
    cursor: pointer;
    padding: 0.625rem 0.75rem;
    border-radius: 0.375rem;
    transition: all 150ms;
    border: 1px solid transparent;
    background: var(--secondaryBgColor-color);

    &:hover {
        background: var(--light-bg);
        box-shadow: 0 0 3px var(--primary-color);
    }

    &.is--active {
        border-color: var(--primary-color);
        background: var(--primary-color-opacity-50);

        .dashboard-template-category-item__name {
            color: var(--primary-color);
            font-weight: 600;
        }

        .dashboard-template-category-item__chevron {
            color: var(--primary-color);
        }
    }

    &__content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
    }

    &__info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-grow: 1;
        min-width: 0;
    }

    &__name {
        font-size: 0.875rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--mainTextColor-color);
    }

    &__count {
        font-size: 0.75rem;
        padding: 0.125rem 0.375rem;
        border-radius: 9999px;
        flex-shrink: 0;
        background: var(--light-bg);
        color: var(--secondary-actions);
    }

    &__chevron {
        flex-shrink: 0;
        transition: color 150ms;
        color: var(--secondary-actions);
    }
}
</style>

